import { useMutation, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import { type Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";

import { productOrdersActions } from "@fitness-app/app-store";

import { productApi, useAppDispatch } from "~/store/initializeStore";

import {
  askAboutDuplicateExtension,
  DuplicateExtensionDismissed,
  isDuplicateExtensionDismissed,
  readDuplicateExtension,
} from "./duplicateExtension";
import {
  askAboutDuplicatePurchase,
  DuplicatePurchaseDismissed,
  isDuplicatePurchaseDismissed,
  readDuplicatePurchase,
} from "./duplicatePurchase";

/** Komunikat z odpowiedzi serwisu, o ile go przysłał. */
const getServerMessage = (error: unknown): string | null => {
  const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;

  return typeof message === "string" && message.trim() ? message : null;
};

export type ConfirmPaymentDocumentIssuance = {
  mode: "auto" | "skip";
  typeOverride?: "receipt" | "invoice";
};

type ConfirmManualPaymentPayload = {
  productId: string;
  orderId: string;
  clientEmail: string;
  /** Brak pola = `auto`: invoices-service wystawia dokument wg ustawień (TRE-2006). */
  documentIssuance?: ConfirmPaymentDocumentIssuance;
};

export const useConfirmManualPayment = (selectedMonth?: Dayjs, ownerId?: string) => {
  const { t } = useTranslation("products");
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const key = "confirmManualPayment";

  return useMutation({
    mutationFn: async (payload: ConfirmManualPaymentPayload) => {
      message.loading({ content: "Trwa przetwarzanie operacji...", key });

      try {
        const response = await productApi.post("/manual-payment/confirm", payload);
        return response.data;
      } catch (error) {
        // Serwis odbija drugie przedłużenie tej samej ceny, dopóki nie ruszyło
        // pierwsze (TRE-2036). To pytanie do operatora, nie awaria: albo
        // łańcuchuje świadomie i ponawiamy z flagą, albo to podwójne kliknięcie
        // i kończymy tutaj — zanim faktura zostanie opłacona, a paragon wystawiony.
        const duplicate = readDuplicateExtension(error);

        if (duplicate) {
          message.destroy(key);

          if (!(await askAboutDuplicateExtension(duplicate))) {
            throw new DuplicateExtensionDismissed();
          }

          message.loading({ content: "Trwa przetwarzanie operacji...", key });
          const response = await productApi.post("/manual-payment/confirm", {
            ...payload,
            allowDuplicateExtension: true,
          });

          return response.data;
        }

        // Druga odmiana tego samego pytania (TRE-2055): klientka ma już opłacone
        // to samo zamówienie sprzed chwili. Zwykle to drugie przejście koszyka
        // — potwierdzenie założyłoby drugą subskrypcję.
        const duplicatePurchase = readDuplicatePurchase(error);

        if (!duplicatePurchase) {
          throw error;
        }

        message.destroy(key);

        if (!(await askAboutDuplicatePurchase(duplicatePurchase))) {
          throw new DuplicatePurchaseDismissed();
        }

        message.loading({ content: "Trwa przetwarzanie operacji...", key });
        const response = await productApi.post("/manual-payment/confirm", {
          ...payload,
          allowDuplicatePurchase: true,
        });

        return response.data;
      }
    },
    onSuccess: (_, payload) => {
      message.destroy(key);
      void message.success(t("messages.success.manualPaymentConfirmed"));
      // Invalidate relevant queries to refetch updated data
      void queryClient.invalidateQueries(["productsTransactions"]);
      if (selectedMonth) {
        void dispatch(productOrdersActions.fetchOrders({ productId: payload.productId, selectedMonth, ownerId }));
      }
    },
    onError: (error) => {
      message.destroy(key);

      // Operator sam wybrał „Nie dodawaj" / „Nie potwierdzaj" — nic nie poszło źle.
      if (isDuplicateExtensionDismissed(error) || isDuplicatePurchaseDismissed(error)) {
        return;
      }

      // Serwis potrafi powiedzieć, DLACZEGO się nie da — np. że faktura została
      // anulowana i raty nie potwierdzi się tą drogą. Generyczny komunikat
      // zamieniał taką informację w „coś poszło nie tak" (TRE-1855).
      void message.error(getServerMessage(error) ?? t("messages.error.manualPaymentConfirmation"));
    },
  });
};
